import { e2eConnector } from './e2e-connector';
import { isOptOutFlagOn } from './feature-flag';
import { CoinsAssetsUsdcIcon, CoinsUsdcIcon, CoinsUsdtIcon } from '@/client/assets';
import { Theme, ThemeMode } from '@/client/hooks/use-theme';
import { APPBaseTokenConfig, Icon } from '@/client/types/app';
import { SupportedLanguage } from '@/libs/sdk/api';
import { parseRouterAddress, resolveRouterConfig, type RouterConfig } from '@/libs/sdk/contracts/core/apmm-router-address';
import { connectorsForWallets } from '@rainbow-me/rainbowkit';
import { tokenPocketWallet, metaMaskWallet, walletConnectWallet } from '@rainbow-me/rainbowkit/wallets';
import { createElement } from 'react';
import type { Address, Hex } from 'viem';
import { Chain, arbitrumSepolia, bsc, bscTestnet } from 'viem/chains';
import { createConfig, fallback, http } from 'wagmi';

// DID token icon
const DidTokenIcon = (props: any) => createElement('img', { ...props, src: '/img/did-token.png', alt: 'DID' });

export const IS_DID = import.meta.env.VITE_BASE_TOKEN_SYMBOL === 'DID';
export const IS_E2E = import.meta.env.VITE_E2E_TEST === 'true';

export const IS_DEV = import.meta.env.DEV;
export const IS_PROD = import.meta.env.PROD;
export const IS_PREVIEW = import.meta.env.MODE === 'preview';
export const IS_DEBUG = IS_DEV || IS_PREVIEW || import.meta.env.VITE_DEBUG === 'true';

// Locale config lives in a wagmi-free module so it can be tested for real.
export { APP_I18N } from './i18n-config';
export const APP_THEME_MODE: ThemeMode = 'dark'; // light | dark | auto
// Tenant theme lock (VITE_THEME_LOCKED=dark|light): hides every theme switch
// and overrides any persisted user choice while the tenant's other theme is
// unfinished. Unset (all other tenants) = current switchable behavior.
const THEME_LOCKED_RAW = import.meta.env.VITE_THEME_LOCKED;
export const APP_THEME_LOCKED: Theme | undefined =
  THEME_LOCKED_RAW === 'dark' || THEME_LOCKED_RAW === 'light' ? THEME_LOCKED_RAW : undefined;

export const TERMS_OF_USE_LINK = '/pdf?file=terms-of-use';
export const PRIVACY_LINK = '/pdf?file=privacy';
// DPIN's own public channels. Channels DPIN doesn't have yet stay empty — every
// render site skips a link with an empty href. Do NOT point any of these back at
// PrometheX (the parent brand).
export const TELEGRAM_LINK = '';
export const DISCORD_LINK = '';
export const TWITTER_LINK = 'https://x.com/Dpincloud';
export const WEBSITE_LINK = '';
export const HELP_LINK = 'https://dpincloud.gitbook.io/dpincloud'; // GitBook docs
export const FEEDBACK_LINK = '';
export const MEDIUM_LINK = 'https://medium.com/@DPinCloud';

// pdf files
export const PDF_FILES = {
  privacy: '/pdfs/privacy-1.0.pdf',
  'terms-of-use': '/pdfs/terms-of-use-1.0.pdf',
} as const;

// open slippage
export const OPEN_SLIPPAGE = true;

// refetchInterval
export const REFETCH_INTERVAL_FAST = 5 * 1e3;
export const REFETCH_INTERVAL_MEDIUM = 10 * 1e3;
export const REFETCH_INTERVAL_SLOW = 15 * 1e3;

// default img
export const DEFAULT_YSE_IMG = '/img/yes.png';
export const DEFAULT_NO_IMG = '/img/no.png';
export const DEFAULT_UP_IMG = '/img/up.png';
export const DEFAULT_DOWN_IMG = '/img/down.png';

// Test market addresses for development
// Market 1: In Progress (before settlement) - Binary
export const TEST_MARKET_ADDRESS = '0x0000000000000000000000000000000000SOL001';
// Market 2: Disputed (in dispute period)
export const TEST_MARKET_ADDRESS_DISPUTED = '0x0000000000000000000000000000000000SOL002';
// Market 3: Closed (final result - YES wins)
export const TEST_MARKET_ADDRESS_CLOSED = '0x0000000000000000000000000000000000SOL003';
// Market 4: Multi-option market (6 options)
export const TEST_MARKET_ADDRESS_MULTI = '0x0000000000000000000000000000MULTI001';

// Array of test markets for easy access by index
export const TEST_MARKET_ADDRESSES = [
  TEST_MARKET_ADDRESS, // index 0: In Progress (Binary)
  TEST_MARKET_ADDRESS_DISPUTED, // index 1: Disputed
  TEST_MARKET_ADDRESS_CLOSED, // index 2: Closed
  TEST_MARKET_ADDRESS_MULTI, // index 3: Multi-option
];

// analytics tokens
export const CLOUDFLARE_ANALYTICS_TOKEN = import.meta.env.VITE_CLOUDFLARE_ANALYTICS_TOKEN;
export const GOOGLE_ANALYTICS_TOKEN = import.meta.env.VITE_GOOGLE_ANALYTICS_TOKEN;
export const UMAMI_ANALYTICS_URL = import.meta.env.VITE_UMAMI_ANALYTICS_URL;
export const UMAMI_ANALYTICS_TOKEN = import.meta.env.VITE_UMAMI_ANALYTICS_TOKEN;
export const AHREFS_ANALYTICS_TOKEN = import.meta.env.VITE_AHREFS_ANALYTICS_TOKEN;

// sentry
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN?.replace('[host]', window.location.host);

export const POINTS_ENABLED = import.meta.env.VITE_POINTS_ENABLED === 'true';

// Opt-out flags: only an off value hides the feature (rule in feature-flag.ts),
// so tenants that never set the key keep the feature as today. The exact
// "false" test comes first because Rollup folds it to a constant: the dpin-bsc
// build then drops the hidden feature's code and lazy chunks. Rollup cannot
// evaluate trim() or toLowerCase(), so other off values hide it at runtime only.

// Comments feature: market opinions, the "Say something" composer, the replies
// sidebar and comment/like notification rows. Off on DPIN (founder, 2026-10-03).
export const COMMENTS_ENABLED =
  import.meta.env.VITE_COMMENTS_ENABLED !== 'false' && isOptOutFlagOn(import.meta.env.VITE_COMMENTS_ENABLED);

// Notifications feature: the header bell, the More-sheet entry, the
// notification routes, their queries and their SSE refresh. Off on DPIN
// (founder, 2026-10-03).
export const NOTIFICATIONS_ENABLED =
  import.meta.env.VITE_NOTIFICATIONS_ENABLED !== 'false' && isOptOutFlagOn(import.meta.env.VITE_NOTIFICATIONS_ENABLED);

// Enrollment is a separate, explicitly enabled capability. Existing rewards
// and referral flags never enable writes to the activity enrollment service.
export const ACTIVITY_ENROLLMENT_ENABLED = import.meta.env.VITE_ACTIVITY_ENROLLMENT_ENABLED === 'true';
// Activity reward distributor: the claim panel sends only when the API names
// this exact address. Never fall back to the referral or another tenant's.
export const ACTIVITY_DISTRIBUTOR_ADDRESS = import.meta.env.VITE_ACTIVITY_DISTRIBUTOR_ADDRESS || '';
// Activity staking contract (DPINStaking) for the in-page stake tab; unset =
// the tab shows an unavailable state instead of writing anywhere.
export const ACTIVITY_STAKING_ADDRESS = import.meta.env.VITE_ACTIVITY_STAKING_ADDRESS || '';
// Activity claim token override, for the testnet tutorial build only (e.g.
// tUSDT on chain 97). Unset/empty = BSC USDT, as the prod build. A value that
// is not a strict 0x address fails closed: claimGate gates every claim as a
// configuration fault (see resolveClaimToken). Never set in .env.dpin-bsc.
export const ACTIVITY_CLAIM_TOKEN = import.meta.env.VITE_ACTIVITY_CLAIM_TOKEN || '';

// Fee-share referral mode (0.5% fee-share, reads /referral/my-earnings). Reuses
// the invite-earn shell but shows accrued fee-share instead of points. Enabled
// independently of POINTS_ENABLED so a tenant can run referral without points.
export const REFERRAL_ENABLED = import.meta.env.VITE_REFERRAL_ENABLED === 'true';

// Referral CLAIM leg kill switch (armed INERT until the audited Merkle
// distributor is deployed and funded -- see S1-S4 in the claim-leg plan).
// Default false: unset/anything-but-"true" renders the locked pill, pixel-
// identical to the pre-claim-API state -- no proof fetch, no transaction.
// FUND-SAFETY RED LINE: do not flip without explicit user go-ahead.
export const REFERRAL_CLAIM_ENABLED = import.meta.env.VITE_REFERRAL_CLAIM_ENABLED === 'true';
// Deployed ReferralMerkleDistributor address. Empty/unset also renders the
// locked pill (belt-and-suspenders with the flag above) -- never derive a
// default from another tenant's address.
export const REFERRAL_DISTRIBUTOR_ADDRESS = (import.meta.env.VITE_REFERRAL_DISTRIBUTOR_ADDRESS || '') as `0x${string}`;

// Per-symbol token icons; unknown symbols fall back to the USDC coin art
const imgTokenIcon = (src: string, alt: string) => (props: any) => createElement('img', { ...props, src, alt });

const tokenIcons = (symbol: string): Pick<APPBaseTokenConfig, 'icon' | 'assetsIcon'> => {
  switch (symbol) {
    case 'DID':
      return { icon: DidTokenIcon, assetsIcon: DidTokenIcon };
    case 'DPS':
      return { icon: imgTokenIcon('/img/dps-token.png', 'DPS'), assetsIcon: imgTokenIcon('/img/dps-token.png', 'DPS') };
    case 'DPN':
      return { icon: imgTokenIcon('/img/dpn-token.png', 'DPN'), assetsIcon: imgTokenIcon('/img/dpn-token.png', 'DPN') };
    default:
      return { icon: CoinsUsdcIcon, assetsIcon: CoinsAssetsUsdcIcon };
  }
};

const BASE_TOKEN_SYMBOL = import.meta.env.VITE_BASE_TOKEN_SYMBOL || 'USDC';
const BASE_TOKEN2_ADDRESS = import.meta.env.VITE_BASE_TOKEN2_ADDRESS;
const BASE_TOKEN2_SYMBOL = import.meta.env.VITE_BASE_TOKEN2_SYMBOL || '';

export const BASE_TOKEN_CONFIG: APPBaseTokenConfig[] = [
  {
    address: import.meta.env.VITE_BASE_TOKEN_ADDRESS || '0x1A8da4723A4f7aD0A965cb3dBC31c72435b1B490',
    ...tokenIcons(BASE_TOKEN_SYMBOL),
    img: '/img/bg-card.png',
    translateKey: BASE_TOKEN_SYMBOL,
    tooltipKey: 'base-token-trading-mode',
    name: import.meta.env.VITE_BASE_TOKEN_NAME || 'USDC',
    symbol: BASE_TOKEN_SYMBOL,
    decimals: Number(import.meta.env.VITE_BASE_TOKEN_DECIMALS || '6'),
  },
  // Optional second base token (multi-base tenants, e.g. DPIN DPS+DPN)
  ...(BASE_TOKEN2_ADDRESS
    ? [
        {
          address: BASE_TOKEN2_ADDRESS,
          ...tokenIcons(BASE_TOKEN2_SYMBOL),
          img: '/img/bg-card.png',
          translateKey: BASE_TOKEN2_SYMBOL,
          tooltipKey: 'base-token-trading-mode',
          name: import.meta.env.VITE_BASE_TOKEN2_NAME || BASE_TOKEN2_SYMBOL,
          symbol: BASE_TOKEN2_SYMBOL,
          decimals: Number(import.meta.env.VITE_BASE_TOKEN2_DECIMALS || '18'),
        },
      ]
    : []),
];

export const DEFAULT_BASE_TOKEN = BASE_TOKEN_CONFIG[0];

export type SiblingSiteToken = { symbol: string; label: string; href: string; icon: Icon };

/** Sibling sites reachable from the token switcher. Full-page links, not lenses:
 *  each is a different SPA on the same origin. Empty on lines that have none. */
export const SIBLING_SITE_TOKENS: SiblingSiteToken[] = [
  { symbol: 'USDT', label: 'USDT', href: '/usdt', icon: CoinsUsdtIcon },
];

// Address comparisons between config (checksummed) and backend/chain sources
// (mixed casing) must be case-insensitive — a strict === lookup misses and
// silently falls back to the wrong token
export const sameAddress = (a?: string, b?: string) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

export const resolveBaseToken = (address?: string): APPBaseTokenConfig | undefined =>
  address ? BASE_TOKEN_CONFIG.find(item => sameAddress(item.address, address)) : undefined;

// CTF (Conditional Token Framework) — ERC-1155 contract for option positions
export const CTF_ADDRESS = import.meta.env.VITE_CTF_ADDRESS as `0x${string}` | undefined;

// APMMRouter (front-end fee router). Off when router and factory are both unset.
// resolveRouterConfig() throws on a half-set or invalid config (and the vite build runs the same check), so the
// feature can not turn on without a factory to verify the router against, and a typo can not silently turn it off.
// ctf is the existing CTF_ADDRESS. A new router generation means a new address here; allowance on an old router never counts.
export const APMM_ROUTER_CONFIG: RouterConfig = resolveRouterConfig({
  router: import.meta.env.VITE_DPIN_ROUTER_ADDRESS,
  factory: import.meta.env.VITE_DPIN_FACTORY_ADDRESS,
  ctf: import.meta.env.VITE_CTF_ADDRESS,
});
// Derived from APMM_ROUTER_CONFIG. Null = feature off.
export const APMM_ROUTER_ADDRESS: Address | null = APMM_ROUTER_CONFIG.status === 'on' ? APMM_ROUTER_CONFIG.router : null;
// Collaterals the client trades (DPS/DPN). buildApprove() uses the intersection with router.allowedCollaterals().
export const APMM_CLIENT_COLLATERALS: Address[] = BASE_TOKEN_CONFIG.flatMap(t => parseRouterAddress(t.address) ?? []);

// Content locale — the language Admin creates content in (for translation source detection)
export const CONTENT_LOCALE = import.meta.env.VITE_CONTENT_LOCALE as SupportedLanguage | undefined;

// Online translation of API text (ApiTranslation and useMarkdown). A tenant whose content ships its own
// i18n text sets VITE_DISABLE_API_TRANSLATION=true. Only the exact value "true" turns it off.
export const API_TRANSLATION_DISABLED = import.meta.env.VITE_DISABLE_API_TRANSLATION === 'true';

/// chain config

// WalletConnect
export const WC_PROJECT_ID = import.meta.env.VITE_WC_PROJECT_ID || '3d70d5d8e49258c37019b5e0afc20ad6';

// export const APP_CHAIN: Chain = IS_DEBUG ? arbitrumSepolia : arbitrum;
const CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || '421614');
const BASE_CHAIN = CHAIN_ID === 97 ? bscTestnet : CHAIN_ID === 56 ? bsc : arbitrumSepolia;

const IS_BSC = CHAIN_ID === 56 || CHAIN_ID === 97;
const IS_BSC_MAINNET = CHAIN_ID === 56;
export const RPC_URLS = [
  import.meta.env.VITE_RPC_URL ||
    import.meta.env.VITE_DEFAULT_RPC_URL ||
    (IS_BSC_MAINNET
      ? 'https://bsc-rpc.publicnode.com'
      : IS_BSC
        ? 'https://bsc-testnet-rpc.publicnode.com'
        : 'https://sepolia-rollup.arbitrum.io/rpc'),
  ...(IS_BSC_MAINNET
    ? ['https://bsc-dataseed1.bnbchain.org']
    : IS_BSC
      ? ['https://data-seed-prebsc-1-s1.bnbchain.org:8545']
      : ['https://arbitrum-sepolia-rpc.publicnode.com']),
];
export const RPC_URL = RPC_URLS[0];
export const APP_CHAIN: Chain = {
  ...BASE_CHAIN,
  rpcUrls: {
    default: {
      http: RPC_URLS,
    },
  },
};

export const WALLET_CONFIG = { chain: APP_CHAIN };

const buildWagmiConfig = () => {
  // E2E mode: use real-signing connector backed by injected private key
  if (IS_E2E) {
    const key = (window as any).__E2E_WALLET_KEY__ as Hex | undefined;
    if (key) {
      return createConfig({
        chains: [APP_CHAIN as any],
        connectors: [e2eConnector({ privateKey: key, chain: APP_CHAIN, rpcUrl: RPC_URL })],
        transports: { [APP_CHAIN.id]: http(RPC_URL) },
      });
    }
  }
  // Production: custom wallet list — TokenPocket first (primary DID BSC wallet)
  const connectors = connectorsForWallets(
    [
      {
        groupName: 'Recommended',
        wallets: [tokenPocketWallet, metaMaskWallet, walletConnectWallet],
      },
    ],
    { appName: 'DPIN Prediction Market', projectId: WC_PROJECT_ID }
  );
  // Same-origin RPC proxy (nginx /rpc) goes FIRST so browser wallet reads ride
  // our own origin; relative paths must not leak into viem transports or
  // wallet-facing chain metadata (RPC_URLS stays absolute for that reason).
  // Falls back to direct publicnode, then dataseed (non-CF, per-client IP
  // quota) if the box or its shared egress ever fails. Browser-only and
  // prod-only: dev servers have no /rpc route.
  const sameOriginRpc =
    typeof window !== 'undefined' && IS_BSC_MAINNET && import.meta.env.PROD ? `${window.location.origin}/rpc` : '';
  return createConfig({
    connectors,
    chains: [APP_CHAIN as any],
    // Fall back through RPC_URLS when the primary RPC errors or times out.
    // E2E branch above keeps a single http(RPC_URL) for test determinism.
    transports: {
      [APP_CHAIN.id]: fallback([...(sameOriginRpc ? [http(sameOriginRpc)] : []), ...RPC_URLS.map(u => http(u))]),
    },
  });
};

export const wagmiConfig = buildWagmiConfig();
